import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AppThunk, RootState } from '../store';
import axios from '../../resources/axios';
import { ManagementCompany, ManagementCompaniesState, ManagementCompanyFormData } from '../../resources/types/managementCompaniesTypes';
import { handleDefaultErrors } from '../../resources/functions';

const initialState: ManagementCompaniesState = {
  companies: [],
  loading: false,
  shouldUpdate: true,
};

export const managementCompaniesSlice = createSlice({
  name: 'managementCompanies',
  initialState,
  reducers: {
    setCompanies: (state, action: PayloadAction<ManagementCompany[]>) => {
      state.companies = action.payload;
      state.shouldUpdate = false;
      state.loading = false;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setShouldUpdate: (state, action: PayloadAction<boolean>) => {
      state.shouldUpdate = action.payload;
    },
  },
});

const { setCompanies, setLoading, setShouldUpdate } = managementCompaniesSlice.actions;

export const getManagementCompaniesAsync = (forceUpdate?: boolean): AppThunk => async (dispatch, getState) => {
  if (!getState().managementCompanies.shouldUpdate && !forceUpdate) return;
  try {
    dispatch(setLoading(true));
    const response = await axios.get('/management_companies');
    dispatch(setCompanies(response.data.data || []));
  } catch (error) {
    dispatch(setLoading(false));
    handleDefaultErrors(error);
  }
};

export const createManagementCompanyAsync = (
  data: ManagementCompanyFormData,
  onSuccess?: () => void
): AppThunk => async dispatch => {
  try {
    dispatch(setLoading(true));
    await axios.post('/management_companies', data);
    dispatch(setShouldUpdate(true));
    await dispatch(getManagementCompaniesAsync(true));
    onSuccess?.();
  } catch (error) {
    dispatch(setLoading(false));
    handleDefaultErrors(error);
  }
};

export const updateManagementCompanyAsync = (
  id: string,
  data: Partial<ManagementCompanyFormData>,
  onSuccess?: () => void
): AppThunk => async dispatch => {
  try {
    dispatch(setLoading(true));
    await axios.put(`/management_companies/${id}`, data);
    dispatch(setShouldUpdate(true));
    await dispatch(getManagementCompaniesAsync(true));
    onSuccess?.();
  } catch (error) {
    dispatch(setLoading(false));
    handleDefaultErrors(error);
  }
};

export const deleteManagementCompanyAsync = (
  id: string,
  onSuccess?: () => void
): AppThunk => async dispatch => {
  try {
    dispatch(setLoading(true));
    await axios.delete(`/management_companies/${id}`);
    dispatch(setShouldUpdate(true));
    await dispatch(getManagementCompaniesAsync(true));
    onSuccess?.();
  } catch (error) {
    dispatch(setLoading(false));
    handleDefaultErrors(error);
  }
};

export const selectManagementCompanies = (state: RootState) => state.managementCompanies;

export default managementCompaniesSlice.reducer;
